/**
 * Records Anomaly Detection Service
 *
 * Analyzes personnel file records to detect anomalies,
 * incomplete records, and potential compliance issues.
 */

export interface AnomalyReport {
  pfileId: string;
  staffName: string;
  fileNumber: string;
  anomalies: Anomaly[];
  completenessScore: number;
  riskLevel: RiskLevel;
  recommendations: string[];
  lastChecked: Date;
}

export interface Anomaly {
  id: string;
  type: AnomalyType;
  severity: AnomalySeverity;
  field?: string;
  message: string;
  details?: string;
  suggestedAction?: string;
}

export type AnomalyType =
  | 'missing_document'
  | 'expired_document'
  | 'incomplete_record'
  | 'date_inconsistency'
  | 'duplicate_record'
  | 'unusual_pattern'
  | 'overdue_checkout'
  | 'missing_approval'
  | 'compliance_violation'
  | 'data_quality';

export type AnomalySeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'none';

// Required documents for a complete PFile
const REQUIRED_DOCUMENTS = [
  { type: 'appointment_letter', name: 'Appointment Letter', critical: true },
  { type: 'acceptance_letter', name: 'Acceptance of Appointment', critical: true },
  { type: 'assumption_duty', name: 'Assumption of Duty', critical: true },
  { type: 'birth_certificate', name: 'Birth Certificate', critical: true },
  { type: 'educational_certificates', name: 'Educational Certificates', critical: true },
  { type: 'ghana_card', name: 'Ghana Card / National ID', critical: true },
  { type: 'passport_photo', name: 'Passport Photograph', critical: false },
  { type: 'medical_report', name: 'Medical Report', critical: false },
  { type: 'next_of_kin', name: 'Next of Kin Declaration', critical: false },
  { type: 'ssnit_registration', name: 'SSNIT Registration', critical: true },
  { type: 'bank_details', name: 'Bank Account Details', critical: true },
];

// Jacket sections that should have documents
const JACKET_SECTIONS = [
  'appointments_promotions',
  'transfers_postings',
  'leave_records',
  'training_development',
  'performance_evaluations',
  'personal_documents',
];

export interface PFileData {
  id: string;
  staffName: string;
  fileNumber: string;
  staffId?: string;
  dateOfBirth?: string;
  appointmentDate?: string;
  retirementDate?: string;
  grade?: number;
  department?: string;
  status: string;
  isCheckedOut: boolean;
  checkedOutAt?: string;
  checkedOutTo?: string;
  documents: DocumentData[];
  jackets: JacketData[];
  createdAt: string;
  updatedAt: string;
}

export interface DocumentData {
  id: string;
  type: string;
  name: string;
  uploadedAt: string;
  expiryDate?: string;
  isVerified: boolean;
}

export interface JacketData {
  id: string;
  sectionType: string;
  documentCount: number;
  lastUpdated: string;
}

class AnomalyDetectionService {
  /**
   * Analyze a single PFile for anomalies
   */
  analyzePFile(pfile: PFileData): AnomalyReport {
    const anomalies: Anomaly[] = [];

    // Run all detection checks
    anomalies.push(...this.checkMissingDocuments(pfile));
    anomalies.push(...this.checkExpiredDocuments(pfile));
    anomalies.push(...this.checkDateInconsistencies(pfile));
    anomalies.push(...this.checkOverdueCheckout(pfile));
    anomalies.push(...this.checkDataQuality(pfile));
    anomalies.push(...this.checkComplianceIssues(pfile));

    // Calculate completeness score
    const completenessScore = this.calculateCompletenessScore(pfile);

    // Determine risk level
    const riskLevel = this.determineRiskLevel(anomalies, completenessScore);

    // Generate recommendations
    const recommendations = this.generateRecommendations(anomalies, pfile);

    return {
      pfileId: pfile.id,
      staffName: pfile.staffName,
      fileNumber: pfile.fileNumber,
      anomalies,
      completenessScore,
      riskLevel,
      recommendations,
      lastChecked: new Date(),
    };
  }

  /**
   * Analyze multiple PFiles and return summary
   */
  analyzeMultiple(pfiles: PFileData[]): {
    reports: AnomalyReport[];
    summary: {
      total: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
      healthy: number;
      averageCompleteness: number;
      topIssues: { type: AnomalyType; count: number }[];
    };
  } {
    const reports = pfiles.map(pfile => this.analyzePFile(pfile));

    // Calculate summary
    const summary = {
      total: reports.length,
      critical: reports.filter(r => r.riskLevel === 'critical').length,
      high: reports.filter(r => r.riskLevel === 'high').length,
      medium: reports.filter(r => r.riskLevel === 'medium').length,
      low: reports.filter(r => r.riskLevel === 'low').length,
      healthy: reports.filter(r => r.riskLevel === 'none').length,
      averageCompleteness: reports.reduce((sum, r) => sum + r.completenessScore, 0) / reports.length,
      topIssues: this.getTopIssues(reports),
    };

    return { reports, summary };
  }

  /**
   * Check for missing required documents
   */
  private checkMissingDocuments(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const existingTypes = new Set(pfile.documents.map(d => d.type.toLowerCase()));

    for (const required of REQUIRED_DOCUMENTS) {
      if (!existingTypes.has(required.type)) {
        anomalies.push({
          id: `missing_${required.type}_${pfile.id}`,
          type: 'missing_document',
          severity: required.critical ? 'high' : 'medium',
          field: required.type,
          message: `Missing ${required.name}`,
          details: `The required document "${required.name}" is not found in this personnel file.`,
          suggestedAction: `Upload ${required.name} to complete the personnel file.`,
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for expired documents
   */
  private checkExpiredDocuments(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const now = new Date();

    for (const doc of pfile.documents) {
      if (doc.expiryDate) {
        const expiryDate = new Date(doc.expiryDate);
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
          anomalies.push({
            id: `expired_${doc.id}`,
            type: 'expired_document',
            severity: 'high',
            field: doc.type,
            message: `${doc.name} has expired`,
            details: `This document expired on ${expiryDate.toLocaleDateString()}`,
            suggestedAction: 'Request updated document from staff member.',
          });
        } else if (daysUntilExpiry <= 30) {
          anomalies.push({
            id: `expiring_${doc.id}`,
            type: 'expired_document',
            severity: 'medium',
            field: doc.type,
            message: `${doc.name} expiring soon`,
            details: `This document will expire in ${daysUntilExpiry} days`,
            suggestedAction: 'Notify staff member to renew document.',
          });
        }
      }
    }

    return anomalies;
  }

  /**
   * Check for date inconsistencies
   */
  private checkDateInconsistencies(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];

    // Check if appointment date is after date of birth
    if (pfile.dateOfBirth && pfile.appointmentDate) {
      const dob = new Date(pfile.dateOfBirth);
      const appointment = new Date(pfile.appointmentDate);
      const ageAtAppointment = (appointment.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365);

      if (ageAtAppointment < 18) {
        anomalies.push({
          id: `age_inconsistency_${pfile.id}`,
          type: 'date_inconsistency',
          severity: 'critical',
          message: 'Age at appointment less than 18 years',
          details: `Staff was ${ageAtAppointment.toFixed(1)} years old at appointment date`,
          suggestedAction: 'Verify date of birth and appointment date.',
        });
      }

      if (ageAtAppointment > 59) {
        anomalies.push({
          id: `late_appointment_${pfile.id}`,
          type: 'date_inconsistency',
          severity: 'medium',
          message: 'Late age at appointment',
          details: `Staff was ${ageAtAppointment.toFixed(1)} years old at appointment date`,
          suggestedAction: 'Verify if this is a contract or special appointment.',
        });
      }
    }

    // Check if retirement date is reasonable
    if (pfile.retirementDate && pfile.dateOfBirth) {
      const dob = new Date(pfile.dateOfBirth);
      const retirement = new Date(pfile.retirementDate);
      const retirementAge = (retirement.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365);

      if (retirementAge !== 60) {
        anomalies.push({
          id: `retirement_age_${pfile.id}`,
          type: 'date_inconsistency',
          severity: 'low',
          message: 'Non-standard retirement age',
          details: `Calculated retirement age is ${retirementAge.toFixed(0)} years`,
          suggestedAction: 'Verify retirement date calculation.',
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for overdue file checkouts
   */
  private checkOverdueCheckout(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];

    if (pfile.isCheckedOut && pfile.checkedOutAt) {
      const checkoutDate = new Date(pfile.checkedOutAt);
      const now = new Date();
      const daysCheckedOut = Math.ceil((now.getTime() - checkoutDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysCheckedOut > 30) {
        anomalies.push({
          id: `overdue_checkout_${pfile.id}`,
          type: 'overdue_checkout',
          severity: daysCheckedOut > 60 ? 'high' : 'medium',
          message: `File checked out for ${daysCheckedOut} days`,
          details: `Checked out to ${pfile.checkedOutTo || 'unknown'} on ${checkoutDate.toLocaleDateString()}`,
          suggestedAction: 'Follow up with custodian to return file.',
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for data quality issues
   */
  private checkDataQuality(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];

    // Check for missing staff ID
    if (!pfile.staffId) {
      anomalies.push({
        id: `missing_staff_id_${pfile.id}`,
        type: 'data_quality',
        severity: 'medium',
        field: 'staffId',
        message: 'Missing Staff ID',
        suggestedAction: 'Update record with official Staff ID.',
      });
    }

    // Check for missing date of birth
    if (!pfile.dateOfBirth) {
      anomalies.push({
        id: `missing_dob_${pfile.id}`,
        type: 'data_quality',
        severity: 'medium',
        field: 'dateOfBirth',
        message: 'Missing Date of Birth',
        suggestedAction: 'Update record with date of birth.',
      });
    }

    // Check for missing grade
    if (!pfile.grade) {
      anomalies.push({
        id: `missing_grade_${pfile.id}`,
        type: 'data_quality',
        severity: 'low',
        field: 'grade',
        message: 'Missing Grade Level',
        suggestedAction: 'Update record with current grade.',
      });
    }

    // Check for empty jackets
    for (const section of JACKET_SECTIONS) {
      const jacket = pfile.jackets.find(j => j.sectionType === section);
      if (!jacket || jacket.documentCount === 0) {
        anomalies.push({
          id: `empty_jacket_${section}_${pfile.id}`,
          type: 'incomplete_record',
          severity: 'low',
          field: section,
          message: `Empty jacket section: ${this.formatSectionName(section)}`,
          suggestedAction: 'Review if documents should be filed in this section.',
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for compliance issues
   */
  private checkComplianceIssues(pfile: PFileData): Anomaly[] {
    const anomalies: Anomaly[] = [];

    // Check if file hasn't been updated in over a year
    const lastUpdate = new Date(pfile.updatedAt);
    const now = new Date();
    const daysSinceUpdate = Math.ceil((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceUpdate > 365) {
      anomalies.push({
        id: `stale_record_${pfile.id}`,
        type: 'compliance_violation',
        severity: 'medium',
        message: 'Record not updated in over a year',
        details: `Last updated ${daysSinceUpdate} days ago`,
        suggestedAction: 'Review and update record with latest information.',
      });
    }

    // Check for unverified documents
    const unverifiedDocs = pfile.documents.filter(d => !d.isVerified);
    if (unverifiedDocs.length > 0) {
      anomalies.push({
        id: `unverified_docs_${pfile.id}`,
        type: 'compliance_violation',
        severity: 'medium',
        message: `${unverifiedDocs.length} unverified document(s)`,
        details: unverifiedDocs.map(d => d.name).join(', '),
        suggestedAction: 'Verify document authenticity.',
      });
    }

    return anomalies;
  }

  /**
   * Calculate completeness score (0-100)
   */
  private calculateCompletenessScore(pfile: PFileData): number {
    let score = 0;
    let maxScore = 0;

    // Required documents (50% of score)
    const existingTypes = new Set(pfile.documents.map(d => d.type.toLowerCase()));
    for (const required of REQUIRED_DOCUMENTS) {
      const weight = required.critical ? 6 : 3;
      maxScore += weight;
      if (existingTypes.has(required.type)) {
        score += weight;
      }
    }

    // Basic data completeness (30% of score)
    const fields = [
      { field: pfile.staffId, weight: 5 },
      { field: pfile.dateOfBirth, weight: 5 },
      { field: pfile.appointmentDate, weight: 5 },
      { field: pfile.grade, weight: 3 },
      { field: pfile.department, weight: 3 },
      { field: pfile.retirementDate, weight: 2 },
    ];

    for (const { field, weight } of fields) {
      maxScore += weight;
      if (field) {
        score += weight;
      }
    }

    // Jacket sections (20% of score)
    for (const section of JACKET_SECTIONS) {
      const weight = 2;
      maxScore += weight;
      const jacket = pfile.jackets.find(j => j.sectionType === section);
      if (jacket && jacket.documentCount > 0) {
        score += weight;
      }
    }

    return Math.round((score / maxScore) * 100);
  }

  /**
   * Determine overall risk level
   */
  private determineRiskLevel(anomalies: Anomaly[], completenessScore: number): RiskLevel {
    const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
    const highCount = anomalies.filter(a => a.severity === 'high').length;
    const mediumCount = anomalies.filter(a => a.severity === 'medium').length;

    if (criticalCount > 0) return 'critical';
    if (highCount >= 3 || completenessScore < 40) return 'high';
    if (highCount > 0 || mediumCount >= 3 || completenessScore < 60) return 'medium';
    if (mediumCount > 0 || completenessScore < 80) return 'low';
    return 'none';
  }

  /**
   * Generate recommendations based on anomalies
   */
  private generateRecommendations(anomalies: Anomaly[], pfile: PFileData): string[] {
    const recommendations: string[] = [];
    const types = new Set(anomalies.map(a => a.type));

    if (types.has('missing_document')) {
      const missing = anomalies.filter(a => a.type === 'missing_document');
      recommendations.push(`Collect ${missing.length} missing document(s) to complete the personnel file.`);
    }

    if (types.has('expired_document')) {
      recommendations.push('Request renewed copies of expired documents from the staff member.');
    }

    if (types.has('overdue_checkout')) {
      recommendations.push(`Recall file from ${pfile.checkedOutTo || 'current custodian'} immediately.`);
    }

    if (types.has('compliance_violation')) {
      recommendations.push('Schedule a compliance review for this personnel file.');
    }

    if (types.has('date_inconsistency')) {
      recommendations.push('Verify all dates in the record against original documents.');
    }

    return recommendations;
  }

  /**
   * Get top issues across all reports
   */
  private getTopIssues(reports: AnomalyReport[]): { type: AnomalyType; count: number }[] {
    const counts = new Map<AnomalyType, number>();

    for (const report of reports) {
      for (const anomaly of report.anomalies) {
        counts.set(anomaly.type, (counts.get(anomaly.type) || 0) + 1);
      }
    }

    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }

  /**
   * Format section name for display
   */
  private formatSectionName(section: string): string {
    return section
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

// Export singleton instance
export const anomalyDetectionService = new AnomalyDetectionService();

export type { AnomalyDetectionService };
